import React from "react";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import SettingsList, { enumOptions, humanize } from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createHelpByAdmin(
    $title: String!
    $description: String!
    $name: HelpName!
    $type: HelpType!
    $order: Int
  ) {
    createHelpByAdmin(
      input: { name: $name, title: $title, description: $description, type: $type, order: $order }
    ) {
      _id
      title
      name
      type
    }
  }
`;

const GET = gql`
  query getHelps($limit: Int, $skip: Int) {
    getHelps(pagination: { limit: $limit, skip: $skip }) {
      _id
      title
      name
      type
      description
      order
    }
  }
`;

const COUNT = gql`
  query getHelpsCount {
    getHelpsCount
  }
`;

const UPDATE = gql`
  mutation updateHelpByAdmin(
    $id: ID!
    $title: String!
    $description: String!
    $name: HelpName!
    $type: HelpType!
    $order: Int
  ) {
    updateHelpByAdmin(
      id: $id
      input: { title: $title, description: $description, name: $name, type: $type, order: $order }
    ) {
      _id
      title
      name
      type
    }
  }
`;

const DELETE = gql`
  mutation removeHelpByAdmin($idSet: [ID!]!) {
    removeHelpByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const TYPES = ["TAXI", "DELIVERY", "FOOD", "DRIVER_RIDE", "DRIVER_DELIVERY", "GROCERY"];

/* The topic slug the app looks the article up by. It is an enum, so a
   typo here means an article nobody can reach — which is why it is a
   picker rather than the free-text box it used to be. */
const NAMES = [
  "I_WANT_TO_REPORT_A_SERVICE_ANIMAL_ISSUE",
  "I_LOST_AN_ITEM",
  "I_WAS_INVOLVED_IN_AN_ACCIDENT",
  "LEARN_ABOUT_SPARK",
  "ACCOUNT_AND_PAYMENT",
  "RIDE_GUIDE",
  "FOOD_GUIDE",
  "GROCERY_SHOPPING_GUIDE",
  "DELIVERY_GUIDE",
  "MY_PARCEL_WAS_NOT_DELIVERED",
  "MY_PARCEL_IS_MISSING_AN_ITEM",
  "MY_DELIVERY_HAS_BEEN_DELAYED",
  "DELIVERY_COSTED_MORE_THAN_ESTIMATED",
  "MY_PACKAGE_WAS_OPENED",
  "OTHER_ISSUES",
  "I_LOST_AN_ITEM_DELIVERY",
  "FOOD_MY_ORDER_COSTED_MORE_THAN_ESTIMATED",
  "FOOD_MY_ORDER_HAS_BEEN_DELAYED",
  "FOOD_MY_ORDER_WAS_DIFFERENT",
  "FOOD_OTHER_ISSUES",
  "GROCERY_MY_ORDER_COSTED_MORE_THAN_ESTIMATED",
  "GROCERY_MY_ORDER_HAS_BEEN_DELAYED",
  "GROCERY_MY_ORDER_WAS_DIFFERENT",
  "GROCERY_OTHER_ISSUES",
  "DRIVER_RIDE_HELP",
  "DRIVER_DELIVERY_HELP",
];

const FIELDS = [
  { name: "title", label: "Title", type: "text", required: true },
  {
    name: "name",
    label: "Topic",
    type: "select",
    required: true,
    options: enumOptions(NAMES),
    hint: "Which question in the app this article answers.",
  },
  {
    name: "type",
    label: "Section",
    type: "select",
    required: true,
    options: enumOptions(TYPES),
  },
  { name: "description", label: "Article", type: "textarea", required: true },
  {
    name: "order",
    label: "Position",
    type: "number",
    defaultValue: 0,
    hint: "Lower numbers appear first within the section.",
  },
];

/**
 * Helps - the help centre articles inside the customer and driver apps.
 */
const Helps = () => (
  <SettingsList
    title="Help articles"
    description="What customers and drivers read when they open help in the app."
    singular="article"
    list={{ query: GET, key: "getHelps" }}
    count={{ query: COUNT, key: "getHelpsCount" }}
    create={{
      mutation: ADD,
      variables: (values) => ({
        title: values.title.trim(),
        description: values.description.trim(),
        name: values.name,
        type: values.type,
        order: values.order === "" ? null : parseInt(values.order, 10),
      }),
    }}
    update={{
      mutation: UPDATE,
      variables: (values, row) => ({
        id: row._id,
        title: values.title.trim(),
        description: values.description.trim(),
        name: values.name,
        type: values.type,
        order: values.order === "" ? null : parseInt(values.order, 10),
      }),
    }}
    remove={{ mutation: DELETE }}
    columns={[
      { key: "title", label: "Title", width: "1.5fr" },
      { key: "name", label: "Topic", width: "1.5fr", render: (row) => humanize(row.name) },
      {
        key: "type",
        label: "Section",
        render: (row) => <StatusBadge status="neutral" label={humanize(row.type)} />,
      },
      {
        key: "description",
        label: "Article",
        width: "2fr",
        render: (row) => <span className="settingsClamp">{row.description}</span>,
      },
    ]}
    fields={FIELDS}
    toValues={(row) => ({
      title: row.title,
      description: row.description,
      name: row.name,
      type: row.type,
      order: row.order === null || row.order === undefined ? "" : row.order,
    })}
  />
);

export default Helps;
